import { TCompanyStore } from 'src/app/Company.store';
import { selectIdPortenUrls } from './eidProviders';

type TAuthenticationUrl = TCompanyStore['data']['authentication_urls'][0];

/**
 * ID-porten single logout.
 *
 * Every other login surface in this portal logs out locally — clear
 * `localStorage`, reload. That is not enough for ID-porten: Digdir keeps a
 * central SSO session of its own, so a citizen who "logs out" while that
 * session lives can press the login button and be back in without ever
 * re-authenticating. On a shared device that is the next person's session.
 * Digdir's `oidc_protocol_logout` says as much — *"A logout will invalidate the
 * central SSO session at ID-porten and log the user out of all other active
 * clients"* — and registering `post_logout_redirect_uris` is required client
 * metadata.
 *
 * The round-trip is the backend's:
 *
 *     GET <platform-host>/idporten/logout?api_key=<token>&redirect_url=<portal>
 *
 * **GET only.** The view reads `api_key`/`redirect_url` from the query string;
 * it carries no `@csrf_exempt`, and this portal is a cross-origin SPA that
 * holds no Django CSRF cookie, so a POST would be rejected by
 * `CsrfViewMiddleware`. Navigate the browser to the URL — do not fetch it: the
 * backend answers 302 to ID-porten's `end_session_endpoint`, which has to run
 * in the top-level browsing context to carry (and then kill) Digdir's SSO
 * cookie. It lands back on `/idporten/logout/complete`, which redirects to
 * `redirect_url` **carrying no credential**.
 *
 * `redirect_url` is the logout's own target, independent of the login's: the
 * backend records the two separately so a citizen who logged in from the iOS
 * app (`wastehero-no://auth-callback`) and logs out in a desktop browser is not
 * sent to a deep link that browser cannot open. The web portal therefore always
 * passes its own origin.
 */

/**
 * Turns an ID-porten *authorize* URL from `authentication_urls` into its
 * sibling *logout* URL on the same backend.
 *
 * Both live at the root of the platform host — `/idporten/auth/start/` and
 * `/idporten/logout` — so the origin is the only part worth carrying over.
 * Deriving it from the authorize URL rather than from
 * `REACT_APP_API_URL` keeps the two ends of one flow on one host even when the
 * dev pill has repointed the API base at another backend (see
 * `getOverrideApiUrl` in src/api/client.tsx).
 *
 * Returns `null` for a URL that cannot be parsed, so a malformed tenant config
 * degrades to a local-only logout instead of throwing inside `logout()`.
 */
export const buildIdPortenLogoutUrl = (
  authorizeUrl: string,
  token: string,
  redirectUrl: string
): string | null => {
  let origin: string;
  try {
    origin = new URL(authorizeUrl).origin;
  } catch {
    return null;
  }
  const params = new URLSearchParams({
    api_key: token,
    redirect_url: redirectUrl,
  });
  return `${origin}/idporten/logout?${params.toString()}`;
};

/**
 * The ID-porten logout URL for this tenant, or `null` when the tenant has no
 * ID-porten entry (so there is no central session to end and a local logout is
 * the whole job).
 */
export const selectIdPortenLogoutUrl = (
  authenticationUrls: readonly TAuthenticationUrl[] | undefined | null,
  token: string | null | undefined,
  redirectUrl: string
): string | null => {
  if (!token) return null;
  const [entry] = selectIdPortenUrls(authenticationUrls);
  if (!entry) return null;
  return buildIdPortenLogoutUrl(entry.url, token, redirectUrl);
};
